import { getDefaultRegistry } from "@rjsf/core";
import type { WidgetProps } from "@rjsf/utils";
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { FolderIcon } from "../../components/skills/icons";
import { FormActionError, runFormAction } from "./formActions";

const Text = getDefaultRegistry().widgets.TextWidget!;

/**
 * A file path the user picks rather than types (`"ui:widget": "filePath"`).
 *
 * The path stays an ordinary text input — it can still be typed or pasted, and it is
 * all there is in a browser or against a desktop older than bridge v10, where
 * `window.bimgo.files` is absent and no button is drawn. With the bridge, the folder
 * button opens the system's file dialog (`ui:options.extensions` filters it), and
 * `ui:options.template` — a file in the workflow kit's `templates/` folder — adds a
 * button that saves a blank copy where the user says and fills the field with it.
 *
 * `ui:options.folder: true` asks for a folder instead of a file (bridge v11); against an
 * older desktop the field stays a plain path input.
 *
 * `ui:options.generate` names an action in the kit's `actions/` folder and adds a
 * button that has the kit write the file from the model open in the selected Revit,
 * using the form's current values. With the field empty it first saves the template
 * where the user says, so the user still chooses the path; with a file already named,
 * the kit decides whether to complete it or write a copy, and the field follows.
 */
export function FilePathWidget(props: WidgetProps) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);
  const bridge = typeof window === "undefined" ? undefined : window.bimgo?.files;
  // A desktop before bridge v10 has `files` for pathFor only: no dialogs, so the plain input stays.
  const files = bridge?.pickFile && bridge.saveTemplate
    ? { pickFile: bridge.pickFile, pickFolder: bridge.pickFolder, saveTemplate: bridge.saveTemplate }
    : undefined;
  const extensions = Array.isArray(props.options.extensions) ? props.options.extensions.map(String) : [];
  const folder = props.options.folder === true;
  const template = typeof props.options.template === "string" ? props.options.template : "";
  const action = typeof props.options.generate === "string" ? props.options.generate : "";
  const context = (props.registry.formContext ?? {}) as {
    workflowId?: string; token?: string | null; orgId?: string | null; sessionId?: string | null; formData?: Record<string, unknown>;
  };
  const workflowId = String(context.workflowId ?? "");
  const locked = props.disabled || props.readonly || busy;
  if (!files || props.readonly || (folder && !files.pickFolder)) return <Text {...props} />;

  /** Why an action failed, in the user's language: the kit's reason when the form knows it, the kind of failure otherwise. */
  const explain = (error: unknown): string => {
    if (!(error instanceof FormActionError)) return t("workSettings.file.failed");
    const byReason = `workSettings.file.reason.${error.reason}`;
    if (error.reason && t(byReason) !== byReason) return t(byReason);
    if (error.status === 409) return t("workSettings.file.revitBusy");
    if (error.status === 502 || error.status === 404) return t("workSettings.file.revitFailed");
    if (error.status === 503) return t("workSettings.file.unavailable");
    return t("workSettings.file.failed");
  };
  const run = (work: () => Promise<void>) => {
    setBusy(true);
    setNote(null);
    void work()
      .catch((error: unknown) => setNote({ text: explain(error), failed: true }))
      .finally(() => setBusy(false));
  };
  const browse = () => run(async () => {
    const picked = folder ? await files.pickFolder?.() : await files.pickFile(extensions);
    if (picked) props.onChange(picked);
  });
  /** The path of a blank copy saved where the user said, or null when they backed out or it failed (and a note says so). */
  const saveBlank = async (): Promise<string | null> => {
    const result = await files.saveTemplate(workflowId, template);
    if ("cancelled" in result && result.cancelled) return null;
    if ("path" in result) return result.path;
    setNote({ text: t(result.error === "write-failed" ? "workSettings.file.templateBusy" : "workSettings.file.failed"), failed: true });
    return null;
  };
  const saveTemplate = () => run(async () => {
    const saved = await saveBlank();
    if (!saved) return;
    props.onChange(saved);
    setNote({ text: t("workSettings.file.templateSaved"), failed: false });
  });
  const generate = () => run(async () => {
    if (!context.token || !context.orgId || !context.sessionId) {
      setNote({ text: t("workSettings.file.generateConnect"), failed: true });
      return;
    }
    const current = typeof props.value === "string" ? props.value.trim().replace(/^"|"$/g, "") : "";
    const target = current || (template ? await saveBlank() : null);
    if (!target) return;
    if (!current) props.onChange(target);
    setGenerating(true);
    const answer = await runFormAction(
      { token: context.token, orgId: context.orgId, sessionId: context.sessionId, workflowId },
      action,
      { values: context.formData ?? {}, path: target },
    ).finally(() => setGenerating(false));
    if (typeof answer["form"] === "string") props.onChange(answer["form"]);
    setNote({
      text: t(answer["copied"] === true ? "workSettings.file.generatedCopy" : "workSettings.file.generated", { types: Number(answer["types"] ?? 0) }),
      failed: false,
    });
  });

  return <>
    <div className="work-settings-file-row">
      <Text {...props} />
      <button type="button" className="work-settings-file-browse" disabled={locked} onClick={browse}
        title={t(folder ? "workSettings.file.browseFolder" : "workSettings.file.browse")}
        aria-label={t(folder ? "workSettings.file.browseFolder" : "workSettings.file.browse")}>
        <FolderIcon size={16} />
      </button>
    </div>
    {workflowId && (action || template) ? <div className="work-settings-file-actions">
      {action ? <button type="button" className="work-settings-file-template" disabled={locked} onClick={generate}>
        {t(generating ? "workSettings.file.generating" : "workSettings.file.generate")}
      </button> : null}
      {template ? <button type="button" className="work-settings-file-template" disabled={locked} onClick={saveTemplate}>
        {t("workSettings.file.template")}
      </button> : null}
    </div> : null}
    {note ? <p className="work-settings-file-note" role={note.failed ? "alert" : "status"}>{note.text}</p> : null}
  </>;
}
